import type { ComponentType } from 'react'
import { useEffect, useState } from 'react'
import * as SplashScreen from 'expo-splash-screen'
import { StartupSurface } from '../components/ui/StartupSurface'
import { isIsolatedPreviewBuild } from '../lib/runtime-build'
import { DURATION } from '../lib/theme/tokens'
import { captureException, initSentry } from '../lib/utils/sentry'

export { ErrorBoundary } from 'expo-router'

void SplashScreen.preventAutoHideAsync().catch(() => {})
SplashScreen.setOptions({ duration: DURATION.fast, fade: true })

// Initialise native Sentry before the full application graph is evaluated.
// This tiny entry module is also the last safe place to report a failure while
// RootLayoutApp is being loaded.
try {
  initSentry()
} catch (error) {
  console.error('[Tago] Sentry initialization failed:', error)
  try {
    captureException(error, { phase: 'root-layout-entry' })
  } catch {
    // Observability must never prevent the startup frame from rendering.
  }
}

type RootAppModule = {
  default: ComponentType
}

function StartupFrame({ failed }: { failed?: boolean }) {
  useEffect(() => {
    // Keep the OS splash in place until RootLayout owns the JS handoff. A fixed
    // timer creates a white gap when module evaluation or font loading takes
    // longer than the native fade. The normal path is dismissed by the font
    // gate in RootLayout; only the recoverable import-failure path needs its
    // own bounded escape hatch.
    if (!failed) return undefined

    const timer = setTimeout(() => {
      void SplashScreen.hideAsync().catch(() => {})
    }, DURATION.normal)

    return () => clearTimeout(timer)
  }, [failed])

  return <StartupSurface failed={failed} />
}

export default function RootLayoutEntry() {
  const [RootApp, setRootApp] = useState<ComponentType | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    let mounted = true

    const loadRootApp = async () => {
      try {
        let recovered = false
        // Give a production native launch one bounded opportunity to consume
        // a rollback directive before evaluating the full Fabric component
        // tree. Dev Client and the physically isolated Preview package cannot
        // receive production OTA rollbacks; importing that recovery graph
        // there merely lengthens the startup handoff and can strand fixture QA
        // behind the launch frame.
        if (!__DEV__ && !isIsolatedPreviewBuild) {
          const { recoverEmbeddedRollback } = await import('../lib/utils/ota-recovery')
          recovered = await recoverEmbeddedRollback('root-entry')
        }
        // reloadAsync schedules a new JS launch; do not import the crashing
        // shell again in the short window before the native reload completes.
        if (recovered || !mounted) return

        // Intentional runtime require: Android must paint StartupFrame before evaluating the app shell.
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const module = require('../components/RootLayoutApp') as RootAppModule
        if (mounted) setRootApp(() => module.default)
      } catch (error) {
        console.error('[Tago] Root app failed to load:', error)
        try {
          captureException(error, { phase: 'root-app-module-load' })
        } catch {
          // Keep the recoverable startup frame available if Sentry is unavailable.
        }
        if (mounted) setLoadFailed(true)
      }
    }

    void loadRootApp()

    return () => {
      mounted = false
    }
  }, [])

  if (RootApp) return <RootApp />
  return <StartupFrame failed={loadFailed} />
}
